import { Controller } from "@hotwired/stimulus"

// Handles:
// - Searching customers via AJAX (returns up to 10)
// - Showing a dropdown of matches
// - Setting hidden customer_id
// - Populating contact fields
export default class extends Controller {
    static values = {
        url: String // /customers.json endpoint
    }

    static targets = [
        "input",         // visible search box
        "results",       // dropdown container
        "hidden",        // hidden customer_id field
        "contactNumber", // contact number input
        "contactPerson", // contact person input
        "address"        // address textarea/input
    ]

    connect() {
        this.abortController = null
        this.hideResults()
    }

    disconnect() {
        if (this.abortController) this.abortController.abort()
    }

    // Fired on input event
    async search() {
        const query = this.inputTarget.value.trim()

        // if empty, hide dropdown
        if (query === "") {
            this.clearResults()
            this.hideResults()
            return
        }

        // cancel previous request if still in-flight
        if (this.abortController) this.abortController.abort()
        this.abortController = new AbortController()

        try {
            const url = new URL(this.urlValue, window.location.origin)
            url.searchParams.set("q", query)

            const response = await fetch(url, { signal: this.abortController.signal })
            if (!response.ok) return

            const customers = await response.json()
            this.renderResults(customers)
        } catch (error) {
            if (error.name !== "AbortError") {
                console.error("Customer search failed", error)
            }
        }
    }

    renderResults(customers) {
        this.clearResults()

        if (!customers || customers.length === 0) {
            this.resultsTarget.insertAdjacentHTML(
                "beforeend",
                `<div class="px-3 py-2 text-sm text-gray-500">No results</div>`
            )
            this.showResults()
            return
        }

        customers.forEach((customer) => {
            const item = document.createElement("button")
            item.type = "button"
            item.className =
                "w-full text-left px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer"
            item.textContent = customer.name

            // Keep full customer payload for selection
            item.addEventListener("mousedown", (event) => {
                event.preventDefault()
                this.selectCustomer(customer)
            })

            this.resultsTarget.appendChild(item)
        })

        this.showResults()
    }

    selectCustomer(customer) {
        // 1. Update visible text
        this.inputTarget.value = customer.name

        // 2. Update hidden customer_id
        if (this.hasHiddenTarget) {
            this.hiddenTarget.value = customer.id

            // Fire change event for any listener
            this.hiddenTarget.dispatchEvent(new Event("change", { bubbles: true }))
        }

        // 3. Populate contact fields IMMEDIATELY
        this.populateContactFields(customer)

        // 4. Hide dropdown
        this.hideResults()
    }

    populateContactFields(customer) {
        if (this.hasContactNumberTarget) {
            this.contactNumberTarget.value = customer.contact_number || ""
        }
        if (this.hasContactPersonTarget) {
            this.contactPersonTarget.value = customer.contact_person || ""
        }
        if (this.hasAddressTarget) {
            this.addressTarget.value = customer.full_address || ""
        }
    }

    clearResults() {
        this.resultsTarget.innerHTML = ""
    }

    showResults() {
        this.resultsTarget.classList.remove("hidden")
    }

    hideResults() {
        this.resultsTarget.classList.add("hidden")
    }

    // Called on blur of the input
    maybeHide() {
        // Give time for mousedown on result to fire before hiding
        setTimeout(() => {
            if (!this.element.contains(document.activeElement)) {
                this.hideResults()
            }
        }, 150)
    }
}
